import type { CSSProperties } from 'react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useContent } from '@/content'
import { usePointerParallax, useScrollStage } from '@/hooks'
import { Icon } from '@/components/ui'
import type { IntroStatus } from '@/features/audio'
import { ROUTES } from '@/lib/paths'
import { INTRO_DURATION, INTRO_IMPACT, useSound } from '@/features/audio'
import styles from './IntroScene.module.css'

/**
 * How long the scene will hold a dark frame waiting for the title cue,
 * keyed on what the audio layer has told it so far.
 *
 * These are ceilings, not delays. The usual path is that the audio layer
 * resolves within a few frames and the scene commits immediately; these only
 * decide how long to wait when something is slow or stuck.
 *
 * `pending` is short because not knowing yet usually means the unlock
 * attempt is about to fail. `armed` is generous because the cue is genuinely
 * coming and the 109 kB it needs may be crossing a slow connection — and
 * having waited, the visitor gets the whole sequence.
 */
/**
 * "Tap" on a touch screen, "Click" on anything with a mouse.
 *
 * Read once at module load rather than watched: a device does not grow a
 * mouse mid-visit, and an instruction that rewrites itself while being read
 * is worse than one word being slightly wrong.
 */
const GESTURE_WORD =
  typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
    ? 'Tap'
    : 'Click'

const HOLD_LIMIT: Record<IntroStatus, number> = {
  pending: 500,
  armed: 3000,
  unavailable: 0,
}

/**
 * - `holding` — waiting to hear whether a title cue is coming. Dark.
 * - `gated`   — the browser refused to start audio. Dark, with an
 *               invitation to click, because a click is the only thing that
 *               can start it and the whole entry is built around the sound.
 *               Only while audio is still locked: once it is running, a
 *               second click buys nothing and the gate would just be a door
 *               in front of a page the visitor has already opened.
 * - `synced`  — the cue is playing; the entry runs on its clock.
 * - `silent`  — nothing to wait for, because sound is switched off. The
 *               short entry runs immediately; nothing is ever gated behind a
 *               click the visitor cannot benefit from.
 */
type Phase = 'holding' | 'gated' | 'synced' | 'silent'

/**
 * The site's entry sequence.
 *
 * The hero frame fades in and zooms out, the greeting rises above the
 * subject's head, and scrolling cross-fades the whole scene into the page —
 * the content does not slide up over it; see `HomePage.module.css`.
 *
 * This carries the page's `h1`, so the sections below must not declare
 * another one.
 *
 * ## Syncing the picture to the sound
 *
 * The title cue is a 7.7-second riser that resolves into an impact at 6.06s.
 * When it plays, the entry runs on *its* clock and for its whole length: the
 * image begins fading up as the first note sounds and finishes arriving as
 * the last one dies away, with the zoom and the copy paced across the same
 * span. The impact at 6.06s is an accent inside that arc — the flash, the
 * zoom kick and the glow on the name all land on it — not the end of it.
 *
 * Picture and sound therefore start together and finish together.
 *
 * ## Why there is a holding phase
 *
 * Browsers block audio until the page has been clicked, tapped or typed into,
 * and a wheel scroll does not count. So at mount we do not yet know whether
 * this visitor will get a 6-second cinematic entry or a 1.9-second silent
 * one — and starting the short one immediately would mean restarting it
 * mid-flight when the sound arrived, which is a visible jump.
 *
 * So the scene holds while the audio layer tries to start — see
 * {@link HOLD_LIMIT} — then commits:
 *
 * - **synced** — the cue is playing; run the long timeline anchored to it.
 * - **silent** — no cue within the window; run the short timeline.
 *
 * Committing to **silent** does not abandon the cue. A browser will not let
 * audio start until the page has been clicked, tapped or typed into, and
 * that gesture usually arrives after the hero has faded in. When it does —
 * and while the visitor is still on the hero, not scrolled past it — the cue
 * plays and the entry re-runs from the top against it, so the two are in
 * step whenever the sound is actually heard.
 *
 * Once the visitor has scrolled into the page the moment has passed, the
 * audio layer refuses the cue, and only a page load brings it back. That is
 * also why returning to the home route from elsewhere in the site replays
 * nothing.
 *
 * With sound switched off there is no holding phase at all.
 */
export const IntroScene = () => {
  const { site } = useContent()
  const stage = useScrollStage<HTMLElement>()
  const parallax = usePointerParallax<HTMLDivElement>()
  const {
    introAt,
    introStatus,
    cancelIntro,
    requestIntro,
    enabled,
    ready,
    supported,
  } = useSound()



  const canSync = enabled && supported
  const [phase, setPhase] = useState<Phase>(() =>
    canSync ? 'holding' : 'silent',
  )

  // Milliseconds from now until the riser starts. Normally a frame or two —
  // the engine schedules a short lookahead — but computed rather than assumed
  // so the visuals track the audio clock instead of drifting from it.
  const [cueDelay, setCueDelay] = useState<number | null>(null)

  // Bumped to replay the entry. Used as a `key`, so the stage remounts and
  // every CSS animation on it starts over — the only reliable way to restart
  // a filled animation, short of toggling each one by hand.
  const [runId, setRunId] = useState(0)

  // Ask for the title cue, naming the route it belongs to.
  //
  // The cue is requested here rather than fired by the audio layer because
  // this scene *is* the thing it scores. On any other route it never mounts,
  // so reloading on `/about` is silent — and the audio layer additionally
  // refuses the request unless this page load actually began on home, so
  // clicking through to home from elsewhere is silent too.
  useEffect(() => {
    requestIntro(ROUTES.home)
  }, [requestIntro])

  // When this instance of the scene appeared.
  //
  // `introAt` stays set for the rest of the page load, so navigating away and
  // back would otherwise hand a freshly mounted scene a cue timestamp from
  // minutes ago and start the six-second timeline over silence. Only a cue
  // that began alongside *this* mount counts.
  const mountedAt = useRef(performance.now())

  /**
   * Whether the cue on the context belongs to *this* mount.
   *
   * `introAt` stays set for the rest of the page load, so navigating away and
   * back would otherwise hand a freshly mounted scene a timestamp from
   * minutes ago. The 250ms grace lets a cue scheduled a frame or two before
   * this component rendered still count as its own.
   */
  const freshCue = introAt !== null && introAt >= mountedAt.current - 250

  // Give up *waiting* and run silently — immediately once the audio layer
  // reports there is nothing to wait for, otherwise on the deadline.
  //
  // Giving up is not the same as giving up for good. Browsers block audio
  // until the page has been clicked, tapped or typed into, so on a first
  // visit this almost always fires; cancelling the cue here meant a visitor
  // who then clicked got silence for the rest of the page load, which is how
  // the intro went missing entirely. The cue stays armed instead, and if it
  // does arrive the entry re-runs from the top against it — see below.
  //
  // It is only cancelled outright when there is genuinely nothing coming:
  // sound switched off, or no Web Audio at all.
  useEffect(() => {
    if (phase !== 'holding') return

    // A cue has arrived for this mount, so the effect below owns the phase.
    //
    // Both fire in the same commit — the audio layer marks the cue spent in
    // the same tick that it reports when it starts — and this one is
    // declared first, so without this guard it would read the spent status,
    // give up, and the scene would run silent through a cue that is already
    // sounding.
    //
    // It has to be the *fresh* test, not merely `introAt !== null`: a stale
    // timestamp from an earlier visit to this route would otherwise block
    // the scene from ever giving up, and it would hold a dark frame forever.
    if (freshCue) return

    if (!canSync) {
      setPhase('silent')
      cancelIntro()
      return
    }

    if (introStatus === 'unavailable') {
      // Nothing is coming. *Why* decides what to show.
      //
      // The gate exists for exactly one reason: to obtain the user gesture a
      // browser demands before it will play audio. If audio is already
      // unlocked — the cue played earlier this page load, and the visitor has
      // since gone to another route and come back — then asking for another
      // click buys nothing and just puts a door in front of the page.
      //
      // `ready` is the difference: true means the audio context is running,
      // so the browser is not the obstacle and there is simply no cue left
      // to wait for.
      setPhase(ready ? 'silent' : 'gated')
      return
    }

    const timer = window.setTimeout(
      () => setPhase(ready ? 'silent' : 'gated'),
      HOLD_LIMIT[introStatus],
    )
    return () => window.clearTimeout(timer)
  }, [phase, canSync, introStatus, freshCue, ready, cancelIntro])

  useEffect(() => {
    if (introAt === null || !freshCue) return

    setCueDelay(Math.max(0, introAt - performance.now()))
    setPhase('synced')
    // Restart the entry so it runs against the cue from the first note.
    //
    // If the scene had already faded in silently — the usual case, because
    // the gesture that unlocks audio arrives after load — this re-runs it.
    // The alternative is a seven-second riser playing over a picture that
    // finished arriving five seconds ago, which is the thing this whole
    // scene exists not to do.
    setRunId((n) => n + 1)
  }, [introAt, freshCue])

  const syncStyle =
    cueDelay === null
      ? undefined
      : ({
          '--sfx-delay': `${Math.round(cueDelay)}ms`,
          '--sfx-length': `${Math.round(INTRO_DURATION * 1000)}ms`,
          '--sfx-impact': `${Math.round(INTRO_IMPACT * 1000)}ms`,
        } as CSSProperties)

  return (
    <>
      {/*
        * The gate is the whole screen, not a label on the hero, and it is
        * portalled to <body> to stay that way.
        *
        * `position: fixed` resolves against the nearest ancestor with a
        * transform, not the viewport — and the page-transition wrapper above
        * this component animates `transform`, so an in-place gate stretched
        * to the full height of the document and left the navigation rail
        * uncovered. A tap there both unveiled *and* navigated away, and since
        * the cue is a one-shot per page load, the intro was then gone for
        * good.
        *
        * `touch-action: pan-y` keeps the page scrollable through it, so
        * nobody is held here who would rather just read the site.
        */}
      {phase === 'gated'
        ? createPortal(
            <button
              type="button"
              data-silent
              className={styles.gate}
              onClick={() => undefined}
            >
              <Icon name="speakerHigh" size={18} aria-hidden="true" />
              <span>{GESTURE_WORD} to unveil me</span>
            </button>,
            document.body,
          )
        : null}

    <section
      className={styles.intro}
      ref={stage.ref}
      aria-label="Introduction"
      data-phase={phase}
      data-sfx={cueDelay === null ? undefined : 'on'}
      style={syncStyle}
    >
      <div className={styles.stage} ref={parallax.ref} key={runId}>
        <div className={styles.frame}>
          <div className={styles.zoom}>
            <img
              src={site.heroImage}
              alt=""
              className={styles.image}
              width={1899}
              height={1236}
              fetchPriority="high"
              decoding="async"
            />
          </div>
          <div className={styles.veil} />
          {/* The riser and its impact, made visible. Inert without sound. */}
          <div className={styles.bloom} aria-hidden="true" />
          <div className={styles.flash} aria-hidden="true" />
        </div>

        <div className={styles.copy}>
          <p className={`t-16 ${styles.greeting}`}>Hello, I&rsquo;m</p>
          <h1 className={styles.name}>{site.owner}</h1>
          <p className={`t-16 ${styles.role}`}>
            I&rsquo;m a Graduate Digital Systems Engineer
          </p>
        </div>

        <div className={styles.cue} aria-hidden="true">
          <span className={`t-12 ${styles.cueLabel}`}>Scroll</span>
          <span className={styles.cueTrack} />
          <Icon name="arrowDown" size={14} />
        </div>
      </div>
    </section>
    </>
  )
}
